import SectionHeading from "@/components/shared/SectionHeading";
import { LoadingGrid, ErrorState, EmptyState } from "@/components/shared/DataState";
import { useAsyncData } from "@/hooks/useAsyncData";
import { getAwards } from "@/data/repository";
import { Trophy } from "lucide-react";

const yearOf = (a) => a.year || (a.awardDate ? String(a.awardDate).slice(0, 4) : "");

// Image-first award tile (certificate scan) with an optional caption.
function AwardImage({ a, idx }) {
  const year = yearOf(a);
  return (
    <figure
      data-testid={`award-card-${idx}`}
      className="reveal mb-6 break-inside-avoid border border-bronze/25 bg-cream overflow-hidden group"
      style={{ transitionDelay: `${(idx % 6) * 80}ms` }}
    >
      <div className="overflow-hidden">
        <img
          src={a.image}
          alt={a.title || "Award"}
          loading="lazy"
          className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      {(a.title || year || a.org) && (
        <figcaption className="px-6 py-5">
          {a.title && <div className="font-serif text-lg text-ink leading-tight">{a.title}</div>}
          {(year || a.org) && (
            <div className="mt-1 text-[0.7rem] tracking-[0.18em] uppercase text-olive">
              {[year, a.org].filter(Boolean).join(" · ")}
            </div>
          )}
        </figcaption>
      )}
    </figure>
  );
}

// Text fallback tile for award entries with no certificate image.
function AwardText({ a, idx }) {
  return (
    <div
      data-testid={`award-card-${idx}`}
      className="reveal mb-6 break-inside-avoid relative border border-bronze/25 bg-beige/60 p-8 overflow-hidden"
      style={{ transitionDelay: `${(idx % 6) * 80}ms` }}
    >
      <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-bronze/10" />
      <Trophy className="text-bronze" size={28} />
      <div className="mt-5 text-[0.7rem] tracking-[0.22em] uppercase text-olive">{yearOf(a)}</div>
      <h3 className="mt-2 font-serif text-2xl text-ink leading-tight">{a.title}</h3>
      {a.org && <div className="mt-1 text-sm italic text-bronze">{a.org}</div>}
      {a.body && <p className="mt-4 text-sm font-light text-ink_soft/80 leading-relaxed">{a.body}</p>}
    </div>
  );
}

export default function AwardsSection({ withHeading = true, background = "bg-beige/60" }) {
  const { data: awards, loading, error, reload } = useAsyncData(getAwards, []);
  return (
    <section data-testid="awards-section" className={`section-pad ${background}`}>
      <div className="container-page">
        {withHeading && (
          <SectionHeading
            eyebrow="Awards & Recognition"
            title={<>Quietly recognized — <span className="italic text-bronze">consistently.</span></>}
            sub="Industry awards are a byproduct of doing right by clients. Here are a few highlights."
          />
        )}

        {loading && (
          <div className={withHeading ? "mt-14" : ""}>
            <LoadingGrid count={4} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" />
          </div>
        )}

        {!loading && error && <ErrorState onRetry={reload} />}

        {!loading && !error && awards.length > 0 && (
          <div className={`${withHeading ? "mt-14" : ""} columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]`}>
            {awards.map((a, i) =>
              a.image ? (
                <AwardImage key={a.id || a.title} a={a} idx={i} />
              ) : (
                <AwardText key={a.id || a.title} a={a} idx={i} />
              )
            )}
          </div>
        )}

        {!loading && !error && awards.length === 0 && (
          <EmptyState title="Awards will appear here." />
        )}
      </div>
    </section>
  );
}
